import React, { useEffect, useState } from "react";
import {
  FaTrash,
  FaFilter,
  FaTimes,
  FaPlus,
  FaWallet,
  FaEdit,
  FaSave,
} from "react-icons/fa";
import {
  depositChfsList,
  addDepositChfs,
  updateDepositChfs,
  updateDepositChfsStatus,
  deleteDepositChfs,
} from "../../api/endpoints"; // Ensure these endpoints are defined in your api/endpoints.js
import Swal from "../../utils/swalCompat";
import PageHeader from "../../components/PageHeader";

// ============================================================
// ✅ ADD / EDIT DEPOSIT CHFS MODAL
// ============================================================
const DepositChfsModal = ({ show, setShow, editData, onSuccess }) => {
  const [formData, setFormData] = useState({
    amount: "",
    bonus_percentage: 0, // Default 0 as requested
  });
  const [saving, setSaving] = useState(false);

  // Reset / Prefill
  useEffect(() => {
    if (show) {
      if (editData) {
        setFormData({
          amount: editData.amount || "",
          bonus_percentage: editData.bonus_percentage || 0,
        });
      } else {
        setFormData({ amount: "", bonus_percentage: 0 });
      }
    }
  }, [show, editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ 
      ...formData, 
      [name]: name === "bonus_percentage" ? (value === "" ? "" : Number(value)) : value 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (!formData.amount || Number(formData.amount) <= 0) {
      Swal.fire("Error", "Please enter a valid amount", "error");
      return;
    }
    if (formData.bonus_percentage === "" || Number(formData.bonus_percentage) < 0) {
      Swal.fire("Error", "Please enter a valid bonus percentage", "error");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        amount: String(formData.amount), // API accepts string based on your request
        bonus_percentage: Number(formData.bonus_percentage),
      };

      let res;
      if (editData) {
        // ✅ UPDATE API
        res = await updateDepositChfs(editData._id, {
          ...payload,
          status: editData.status, // Keep existing status
        });
      } else {
        // ✅ ADD API
        res = await addDepositChfs(payload);
      }

      console.log("API Response:", res);

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: editData ? "Updated!" : "Added!",
          text: `Deposit Chfs ${editData ? "updated" : "added"} successfully`,
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        setShow(false);
        if (onSuccess) onSuccess();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: res.message || "Operation failed",
        });
      }
    } catch (error) {
      console.error("Deposit Chfs save error:", error);
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: error.response?.data?.message || "Failed to save. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
      tabIndex="-1"
      onClick={() => setShow(false)}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0" style={{ borderRadius: 14 }}>
          {/* Header */}
          <div
            className="modal-header border-0 text-white"
            style={{
              background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
              borderTopLeftRadius: 14,
              borderTopRightRadius: 14,
              padding: "16px 20px",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{
                  width: 36,
                  height: 36,
                  background: "rgba(255,255,255,0.2)",
                }}
              >
                <FaWallet size={15} />
              </div>
              <div>
                <h5 className="modal-title fw-bold mb-0">
                  {editData ? "Edit Deposit Chfs" : "Add Deposit Chfs"}
                </h5>
                <small style={{ opacity: 0.85, fontSize: "0.72rem" }}>
                  {editData ? "Update deposit details" : "Add a new deposit scheme"}
                </small>
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setShow(false)}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body p-4">
            {/* Amount */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Amount <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                name="amount"
                className="form-control form-control-lg"
                placeholder="e.g. 10000"
                value={formData.amount}
                onChange={handleChange}
                disabled={saving}
                autoFocus
              />
            </div>

            {/* Bonus Percentage */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Bonus Percentage (%) <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                name="bonus_percentage"
                className="form-control form-control-lg"
                placeholder="e.g. 5.5"
                step="0.1"
                value={formData.bonus_percentage}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            {/* Info note */}
            {!editData && (
              <div
                className="p-3 rounded-3 small"
                style={{ background: "#FEF3C7", color: "#B45309" }}
              >
                <strong>ℹ️ Note:</strong> New Deposit Chfs will be added as{" "}
                <strong>ACTIVE</strong> by default.
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="modal-footer border-0"
            style={{ padding: "12px 20px 18px" }}
          >
            <button
              type="button"
              className="btn btn-light border"
              onClick={() => setShow(false)}
              disabled={saving}
              style={{ fontWeight: 600 }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleSubmit}
              disabled={saving}
              style={{
                background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                border: "none",
                fontWeight: 600,
                paddingLeft: 20,
                paddingRight: 20,
              }}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  {editData ? <FaSave size={12} /> : <FaPlus size={12} />}
                  {editData ? "Update" : "Add Deposit"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const DepositChfsSetting = () => {
  const [chfsData, setChfsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Filters
  const [showFilter, setShowFilter] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  // ============================================================
  // FETCH DEPOSIT CHFS LIST
  // ============================================================
  const fetchChfsList = async (pageNum = page) => {
    try {
      setLoading(true);
      const params = { page: pageNum, limit };

      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;

      console.log("Fetching Deposit Chfs list with params:", params);
      const res = await depositChfsList(params);
      console.log("Deposit Chfs List Response:", res);

      if (res.success) {
        // API response structure based on your JSON
        const { upis = [], pagination = {} } = res.data || {};
        setChfsData(upis);
        setTotalPages(pagination.totalPages || 1);
        setTotalItems(pagination.total || 0);
        setPage(pagination.page || 1);
      } else {
        Swal.fire("Error", res.message || "Failed to fetch list", "error");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to fetch list",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChfsList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Debounced search + status filter
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search || statusFilter) {
        setPage(1);
        fetchChfsList(1);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  // ============================================================
  // LOCAL FILTER (extra safety)
  // ============================================================
  const filteredList = chfsData.filter((item) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      String(item.amount).includes(q) ||
      String(item.bonus_percentage).includes(q);

    const matchesStatus = !statusFilter || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const hasActiveFilters = search || statusFilter;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  // ============================================================
  // ADD / EDIT
  // ============================================================
  const handleOpenAdd = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditData(item);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    fetchChfsList(page);
  };

  // ============================================================
  // DELETE — API
  // ============================================================
  const handleDelete = async (id, amount) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete Deposit of "${amount}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      setDeletingId(id);
      const res = await deleteDepositChfs(id);
      console.log("Delete response:", res);

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: res.message || "Deleted successfully",
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        fetchChfsList(page);
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: res.message || "Failed to delete",
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: error.response?.data?.message || "Failed to delete",
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ============================================================
  // STATUS TOGGLE — API
  // ============================================================
  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const action = newStatus === "ACTIVE" ? "activate" : "deactivate";

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to ${action} this Deposit Chfs?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: newStatus === "ACTIVE" ? "#28a745" : "#d33",
      confirmButtonText: `Yes, ${action}`,
    });

    if (!result.isConfirmed) return;

    try {
      setTogglingId(id);
      const res = await updateDepositChfsStatus(id, newStatus);
      console.log("Status update response:", res);

      if (res.success) {
        setChfsData((prev) =>
          prev.map((item) =>
            item._id === id ? { ...item, status: newStatus } : item,
          ),
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Status ${action}d successfully`,
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });

        fetchChfsList(page);
      } else {
        Swal.fire({
          icon: "error",
          title: "Cannot Change Status",
          text: res.message || `Failed to ${action}`,
        });
      }
    } catch (error) {
      console.error("Status update error:", error);
      Swal.fire({
        icon: "error",
        title: "Cannot Change Status",
        text: error.response?.data?.message || `Failed to ${action}. Please try again.`,
      });
    } finally {
      setTogglingId(null);
    }
  };

  // ============================================================
  // PAGINATION
  // ============================================================
  const goToPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) setPage(pageNum);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ============================================================
  // FORMAT DATE
  // ============================================================
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="lead-page">
      {/* Header */}
      <PageHeader
        title="Deposit Chfs Management"
        subtitle="Manage all Deposit Chfs schemes"
        actions={
          <>
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleOpenAdd}
            >
              <FaPlus size={12} /> Add Deposit Chfs
            </button>
          </>
        }
      />

      {/* Main Card */}
      <div className="card mb-4 card-light">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="card-title mb-0">Deposit Chfs List</h5>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {showFilter && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search amount or %..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: "220px" }}
                />

                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ width: "140px" }}
                >
                  <option value="">All Status</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>

                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    <FaTimes /> Clear
                  </button>
                )}
              </div>
            )}
            <button
              className={`btn ${showFilter ? "btn-primary" : "btn-dark"}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter />
              {showFilter ? " Hide" : " Filter"}
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Info row */}
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <span className="text-muted">
                Showing {filteredList.length} of {totalItems} entries
              </span>
              {hasActiveFilters && (
                <span className="badge bg-info ms-2">Filters Applied</span>
              )}
            </div>
            <div>
              <span className="text-muted">
                Page {page} of {totalPages}
              </span>
            </div>
          </div>

          {/* Active filter badges */}
          {hasActiveFilters && (
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {search && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Search: {search}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => setSearch("")}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Status: {statusFilter}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => setStatusFilter("")}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Table */}
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Amount</th>
                  <th>Bonus %</th>
                  <th>Bonus Amount</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && chfsData.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredList.length > 0 ? (
                  filteredList.map((item, index) => (
                    <tr key={item._id}>
                      <td>{(page - 1) * limit + index + 1}</td>

                      {/* Amount */}
                      <td>
                        <span className="fw-semibold">₹{item.amount}</span>
                      </td>

                      {/* Bonus % */}
                      <td>{item.bonus_percentage}%</td>

                      {/* Bonus Amount */}
                      <td>₹{item.bonus_amount}</td>

                      {/* Status Toggle + Badge */}
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <label
                            style={{
                              position: "relative",
                              display: "inline-block",
                              width: "50px",
                              height: "24px",
                              marginBottom: 0,
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.status === "ACTIVE"}
                              onChange={() =>
                                handleStatusToggle(item._id, item.status)
                              }
                              disabled={togglingId === item._id}
                              style={{
                                opacity: 0,
                                width: 0,
                                height: 0,
                              }}
                            />
                            <span
                              style={{
                                position: "absolute",
                                cursor: "pointer",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor:
                                  item.status === "ACTIVE" ? "#4CAF50" : "#ccc",
                                transition: ".4s",
                                borderRadius: "34px",
                                opacity: togglingId === item._id ? 0.5 : 1,
                              }}
                            >
                              <span
                                style={{
                                  position: "absolute",
                                  height: "16px",
                                  width: "16px",
                                  left:
                                    item.status === "ACTIVE" ? "26px" : "4px",
                                  bottom: "4px",
                                  backgroundColor: "white",
                                  transition: ".4s",
                                  borderRadius: "50%",
                                }}
                              />
                            </span>
                          </label>
                          <span
                            className={`badge ${
                              item.status === "ACTIVE"
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                            style={{ minWidth: "70px" }}
                          >
                            {item.status}
                          </span>
                          {togglingId === item._id && (
                            <span
                              className="spinner-border spinner-border-sm text-primary"
                              role="status"
                            ></span>
                          )}
                        </div>
                      </td>

                      {/* Created */}
                      <td>{formatDate(item.createdAt)}</td>

                      {/* Actions */}
                      <td>
                        <div className="d-flex gap-2 justify-content-center">
                          <button
                            className="btn btn-sm btn-info custum_edit text-white"
                            onClick={() => handleOpenEdit(item)}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(item._id, item.amount)}
                            disabled={deletingId === item._id}
                            title="Delete"
                          >
                            {deletingId === item._id ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                              ></span>
                            ) : (
                              <FaTrash />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      {hasActiveFilters
                        ? "No entries found matching your filters"
                        : "No Deposit Chfs Found — Add your first entry above"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>

              <div className="d-flex gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn ${
                      pageNum === page ? "btn-primary" : "btn-outline-primary"
                    }`}
                    onClick={() => goToPage(pageNum)}
                    disabled={loading}
                    style={{ minWidth: "40px" }}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}

          <div className="d-flex justify-content-center mt-2">
            <small className="text-muted">
              Showing {filteredList.length} of {totalItems} entries | Page{" "}
              {page} of {totalPages}
            </small>
          </div>
        </div>
      </div>

      {/* Modal */}
      <DepositChfsModal
        show={showModal}
        setShow={setShowModal}
        editData={editData}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default DepositChfsSetting;